// State-machine mappers — ported faithfully from the MT5-Fleet-Console mockup.
//
// The dashboard derives, per account, five INDEPENDENT status dimensions that are never
// merged (connection · broker link · risk · trading · data quality), plus the two live
// ages (heartbeat/received, quote). Liveness is by SERVER RECEIVE TIME: the store keeps an
// estimate of "server now" and ages are `serverNow - received_at`, recomputed locally
// between SSE updates so a stalled account drifts live → delayed → stale → offline on its
// own. Risk is threshold-driven on the client so the Settings thresholds recompute the risk
// pills, quick-filter counts and coloring immediately (matching the mockup).

import type {
  AccountRow,
  BrokerLink,
  Connection,
  DataQuality,
  Risk,
  Trading,
} from '../types/api';
import { toNum } from './decimal';
import { parseTs } from './format';
import { BM, CM, QM, RM, StateToken, TM } from './tokens';

export interface Thresholds {
  /** Margin level warning below (percent). */
  mW: number;
  /** Margin level critical below (percent). */
  mC: number;
  /** Drawdown warning above (percent). */
  dW: number;
  /** Drawdown critical above (percent). */
  dC: number;
  /** Daily loss warning above (percent of day-start equity). */
  lW: number;
}

export const DEFAULT_THRESHOLDS: Thresholds = { mW: 400, mC: 150, dW: 10, dC: 25, lW: 4 };

const CONN_SEVERITY: Record<Connection, number> = { live: 0, delayed: 1, stale: 2, offline: 3 };

/** Classify received-age (ms) into a connection state — the mockup's thresholds. */
export function connFromAge(ageMs: number | null): Connection {
  if (ageMs == null || !Number.isFinite(ageMs)) return 'offline';
  if (ageMs < 10000) return 'live';
  if (ageMs < 60000) return 'delayed';
  if (ageMs < 180000) return 'stale';
  return 'offline';
}

/** Worse of two connection states (higher severity wins). */
function worseConn(a: Connection, b: Connection): Connection {
  return CONN_SEVERITY[a] >= CONN_SEVERITY[b] ? a : b;
}

/**
 * Effective connection = worse of the age-derived state and the server's last-known state.
 * The server may know an account is offline for reasons other than age (collector/VPS down);
 * age may know it has gone stale since the last frame. We surface the worse of the two.
 */
export function effConn(row: AccountRow, receivedAgeMs: number | null): Connection {
  return worseConn(connFromAge(receivedAgeMs), row.connection);
}

export function bcOf(row: AccountRow): BrokerLink {
  return row.broker_link;
}

export function trOf(row: AccountRow): Trading {
  return row.trading;
}

/** Data quality collapses to "unknown" once the feed is stale/offline — nothing is assumed current. */
export function dqOf(row: AccountRow, conn: Connection): DataQuality {
  if (row.data_quality !== 'current') return row.data_quality;
  return conn === 'stale' || conn === 'offline' ? 'unknown' : 'current';
}

/**
 * Threshold-driven risk, identical to the mockup `riskOf`. Returns [risk, reason].
 * Numeric fields are parsed from their decimal strings; nulls are treated as "unknown"
 * and skipped (never coerced to a misleading zero).
 */
export function riskOf(row: AccountRow, thr: Thresholds): [Risk, string] {
  const bal = toNum(row.balance);
  const eq = toNum(row.equity);
  const mlvl = toNum(row.margin_level);
  const dd = toNum(row.drawdown_pct);
  const dailyPct = toNum(row.daily_pct);

  if ((bal != null && bal < 0) || (eq != null && eq <= 0)) return ['critical', 'Equity depleted'];
  if (mlvl != null && mlvl < thr.mC) return ['critical', 'Margin ' + Math.round(mlvl) + '%'];
  if (dd != null && dd > thr.dC) return ['critical', 'DD ' + dd.toFixed(1) + '%'];
  if (mlvl != null && mlvl < thr.mW) return ['warning', 'Margin ' + Math.round(mlvl) + '%'];
  if (dd != null && dd > thr.dW) return ['warning', 'DD ' + dd.toFixed(1) + '%'];
  if (dailyPct != null && dailyPct < -thr.lW) return ['warning', 'Day ' + dailyPct.toFixed(1) + '%'];
  if (row.dup_terminal) return ['warning', 'Dual terminal'];
  return ['normal', ''];
}

/** Priority rank for the default "exception-first" sort (0 = most urgent). */
export function rankOf(conn: Connection, bc: BrokerLink, risk: Risk): number {
  if (risk === 'critical') return 0;
  if (conn === 'offline' || conn === 'stale') return 1;
  if (risk === 'warning' || bc === 'disconnected' || conn === 'delayed') return 2;
  return 3;
}

export interface Derived {
  conn: Connection;
  bc: BrokerLink;
  tr: Trading;
  risk: Risk;
  why: string;
  dq: DataQuality;
  /** ms since server received the last frame (liveness). */
  hbAgeMs: number | null;
  /** ms since the last broker quote. */
  qtAgeMs: number | null;
  receivedAtMs: number | null;
  heartbeatAtMs: number | null;
  quoteAtMs: number | null;
  rank: number;
  // Parsed numerics for display / coloring / sorting (never used for exact sums).
  balNum: number | null;
  eqNum: number | null;
  fltNum: number | null;
  dailyNum: number | null;
  dailyPctNum: number | null;
  ddNum: number | null;
  mlvlNum: number | null;
  fmgNum: number | null;
  lotsNum: number | null;
  // Status tokens.
  cm: StateToken;
  bm: StateToken;
  rm: StateToken;
  tm: StateToken;
  qm: StateToken;
}

/**
 * Full per-account derivation. `serverNowMs` is the store's estimate of current server time
 * (local clock + measured offset), so ages keep counting between updates.
 */
export function derive(row: AccountRow, serverNowMs: number, thr: Thresholds): Derived {
  const receivedAtMs = parseTs(row.received_at);
  const heartbeatAtMs = parseTs(row.heartbeat_at);
  const quoteAtMs = parseTs(row.quote_at);

  const hbAgeMs = receivedAtMs == null ? null : Math.max(0, serverNowMs - receivedAtMs);
  const qtAgeMs = quoteAtMs == null ? null : Math.max(0, serverNowMs - quoteAtMs);

  const conn = effConn(row, hbAgeMs);
  const bc = bcOf(row);
  const tr = trOf(row);
  const [risk, why] = riskOf(row, thr);
  const dq = dqOf(row, conn);

  return {
    conn,
    bc,
    tr,
    risk,
    why,
    dq,
    hbAgeMs,
    qtAgeMs,
    receivedAtMs,
    heartbeatAtMs,
    quoteAtMs,
    rank: rankOf(conn, bc, risk),
    balNum: toNum(row.balance),
    eqNum: toNum(row.equity),
    fltNum: toNum(row.floating_pl),
    dailyNum: toNum(row.daily_pl),
    dailyPctNum: toNum(row.daily_pct),
    ddNum: toNum(row.drawdown_pct),
    mlvlNum: toNum(row.margin_level),
    fmgNum: toNum(row.free_margin),
    lotsNum: toNum(row.open_lots),
    cm: CM[conn]!,
    bm: BM[bc]!,
    rm: RM[risk]!,
    tm: TM[tr]!,
    qm: QM[dq]!,
  };
}
